import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, clearSession } from '../api/client';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const qc = useQueryClient();
  const [token, setToken] = useState(() => localStorage.getItem('erp_token') || null);
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('erp_user') || 'null');
    } catch {
      return null;
    }
  });
  const [companyId, setCompanyId] = useState(() => localStorage.getItem('erp_company_id') || null);
  const [fyId, setFyId] = useState(() => localStorage.getItem('erp_fy_id') || null);
  const [ready, setReady] = useState(false);

  // Rehydrate session on mount.
  useEffect(() => {
    (async () => {
      if (!token) {
        setReady(true);
        return;
      }
      try {
        const data = await apiGet('/auth/me');
        const u = data.user;
        setUser(u);
        localStorage.setItem('erp_user', JSON.stringify(u));
        if (!companyId && u.companies?.length) {
          const first = u.companies[0];
          localStorage.setItem('erp_company_id', String(first.company.id));
          setCompanyId(String(first.company.id));
          if (first.activeFinancialYearId) {
            localStorage.setItem('erp_fy_id', String(first.activeFinancialYearId));
            setFyId(String(first.activeFinancialYearId));
          }
        }
      } catch {
        /* interceptor handles logout */
      } finally {
        setReady(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback((t, u) => {
    // Start from nothing. Whatever the last person left behind — a token, a
    // half-finished step-in, answers cached under their name — must not be
    // read as this person's.
    clearSession();
    qc.clear();
    localStorage.setItem('erp_token', t);
    setToken(t);
    setUser(u);
    localStorage.setItem('erp_user', JSON.stringify(u));
    const first = u.companies?.[0];
    const cid = first ? String(first.company.id) : null;
    if (cid) {
      localStorage.setItem('erp_company_id', cid);
      setCompanyId(cid);
      const fy = first.activeFinancialYearId;
      if (fy) {
        localStorage.setItem('erp_fy_id', String(fy));
        setFyId(String(fy));
      } else {
        localStorage.removeItem('erp_fy_id');
        setFyId(null);
      }
    }
  }, [qc]);

  const logout = useCallback(() => {
    // Everything, including a supervisor's step-in. Leaving that behind let
    // the next person on this browser press "Back to my screen" and land in
    // the supervisor's session.
    clearSession();
    qc.clear();
    setToken(null);
    setUser(null);
    setCompanyId(null);
    setFyId(null);
  }, [qc]);

  const selectCompany = useCallback(async (cid) => {
    localStorage.setItem('erp_company_id', String(cid));
    setCompanyId(String(cid));
    // pick active FY for that company if available
    const u = JSON.parse(localStorage.getItem('erp_user') || 'null');
    const mem = u?.companies?.find((m) => m.company.id === Number(cid));
    if (mem?.activeFinancialYearId) {
      localStorage.setItem('erp_fy_id', String(mem.activeFinancialYearId));
      setFyId(String(mem.activeFinancialYearId));
    } else {
      try {
        const { financialYears } = await apiGet(`/companies/${cid}/financial-years`);
        const last = financialYears[financialYears.length - 1];
        if (last) {
          localStorage.setItem('erp_fy_id', String(last.id));
          setFyId(String(last.id));
          await apiPost(`/companies/${cid}/financial-year/select`, { financialYearId: last.id });
        }
      } catch {
        /* ignore */
      }
    }
  }, []);

  const selectFY = useCallback(async (fid) => {
    localStorage.setItem('erp_fy_id', String(fid));
    setFyId(String(fid));
    if (companyId) {
      try {
        await apiPost(`/companies/${companyId}/financial-year/select`, { financialYearId: fid });
      } catch {
        /* ignore */
      }
    }
  }, [companyId]);

  const changePassword = useCallback(
    (body) => apiPost('/auth/change-password', body),
    [],
  );

  const memberships = useMemo(() => user?.companies || [], [user]);
  const currentCompany = useMemo(
    () => memberships.find((m) => m.company.id === Number(companyId)) || null,
    [memberships, companyId],
  );
  const role = currentCompany?.role || 'VIEWER';

  const value = {
    token,
    user,
    ready,
    login,
    logout,
    changePassword,
    selectCompany,
    selectFY,
    companyId,
    fyId,
    memberships,
    currentCompany,
    role,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);